import { allUserCache, refreshAllUserCache } from '../data/all-user-cache';
import { cache } from '../../supplier/cache-impl/cache-registry';
import { customElement, property, state } from 'lit/decorators.js';
import { DataTableWrapper } from '../../../webmodule-common/other/ui/datatable-view';
import { DateTime } from 'luxon';
import { defaultStagedThreshold } from '../../../webmodule-common/other/general/staged-event';
import { EventSourceHandler, stagedExecution } from '../../../webmodule-common/other/general/event-source-client';
import { getApiFactory } from '../../api/api-injector';
import { getQuoteNumberDisplay } from '../data/quote-helper';
import { getSupportTypeDisplayValue, getSupportTypeStatus, getSupportTypeStatusClass } from '../data/support-helper';
import { html, TemplateResult } from 'lit';
import { information } from '../../../webmodule-common/other/ui/modal-option';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { lockUIandExecute } from '../../../webmodule-common/other/ui-lock';
import { notificationSignal } from '../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { RequestPage, ResultPaginated } from '../../../webmodule-common/other/ui/RequestPage';
import {
  SupportInboxItemStatus,
  SupportInboxItemType,
  SupportInboxTicketOwnerFilter,
  ViewSupportInboxItemEx
} from '../../api/supplier-api-interface-franchiseenetwork';
import {
  serverDateTimeToLocal,
  serverDateTimeToLocalDateTime
} from '../../../webmodule-common/other/datetime-converter';
import { tlang } from '../../../webmodule-common/other/language/lang';
import { userDataStore } from '../../supplier/common/current-user-data-store';
import { WebModuleTable, WebModuleTableColumnDef } from '../../../webmodule-common/components/src/webmodule-components';
import { WMEventSource } from '../../api/event-source';

import { SSESupportInbox } from '../data/sse-support-inbox';
import { SSEUserRefresh } from '../data/sse-user-refresh';

@customElement('wm-webmodulesupplierquotesupportinboxview')
export class WebModuleSupplierQuoteSupportInboxView extends DataTableWrapper<ViewSupportInboxItemEx> {
  private franchiseeApi = getApiFactory().franchisee();
  sseHandler?: EventSourceHandler;
  sseUserRefreshHandler?: EventSourceHandler;

  // ---- Filters ----
  // Ticket owner (All / Mine / Unassigned) and the free-text search are owned by
  // this table now (rendered in the DataTableWrapper filter row), matching the
  // dealer support inbox. typeFilter / statusFilter / show* are still configured
  // by the parent tab.
  @state()
  private _ticketOwner: SupportInboxTicketOwnerFilter = SupportInboxTicketOwnerFilter.All;
  private get ticketOwner(): SupportInboxTicketOwnerFilter {
    return this._ticketOwner;
  }

  private set ticketOwner(value: SupportInboxTicketOwnerFilter) {
    if (this._ticketOwner !== value) {
      this._ticketOwner = value;
      this.resetPageAndRefresh();
    }
  }

  private filterText: string | null = null;

  @property()
  private _activeTerritoryBranchIds?: string[] | undefined;
  public get activeTerritoryBranchIds(): string[] | undefined {
    return this._activeTerritoryBranchIds;
  }

  public set activeTerritoryBranchIds(value: string[] | undefined) {
    if (this._activeTerritoryBranchIds !== value) {
      this._activeTerritoryBranchIds = value;
      this.resetPageAndRefresh();
    }
  }

  @property()
  private _typeFilter?: SupportInboxItemType | undefined;
  public get typeFilter(): SupportInboxItemType | undefined {
    return this._typeFilter;
  }

  public set typeFilter(value: SupportInboxItemType | undefined) {
    if (this._typeFilter === value) return;
    this._typeFilter = value;
    this.resetPageAndRefresh();
  }

  @property()
  private _statusFilter?: SupportInboxItemStatus | undefined;
  public get statusFilter(): SupportInboxItemStatus | undefined {
    return this._statusFilter;
  }

  public set statusFilter(value: SupportInboxItemStatus | undefined) {
    if (this._statusFilter === value) return;
    this._statusFilter = value;
    this.resetPageAndRefresh();
  }

  @property()
  showResolvedColumns?: boolean;
  @property()
  showLastModified?: boolean;
  @property()
  showAssignedTo?: boolean;

  override pageLength(): number {
    return 25;
  }

  // The inbox tabs are a fixed-height region; let the table own the vertical scroll
  // so a second (tab) scrollbar does not appear beside it. The fill-height flex chain
  // for the host is set up in dealer_ui.scss (class-based, shared with the supplier UI).
  override fillHeight(): boolean {
    return true;
  }

  override getRowKey(item: ViewSupportInboxItemEx): unknown {
    return item.item.id;
  }

  override enableFiltering(): boolean {
    return true;
  }

  override updateFilter(searchTerm: string | null) {
    // The wrapper's filter input calls this then re-queries from page 1; just store the term.
    this.filterText = searchTerm;
  }

  override enableAdvancedFiltering(): boolean {
    return true;
  }

  override advancedFilterTemplate(): TemplateResult {
    const checkTemplate = (label: string, value: SupportInboxTicketOwnerFilter) => {
      const eventClick = (e: Event) => {
        e.stopImmediatePropagation();
        this.ticketOwner = value;
      };
      const icon = value === this.ticketOwner ? 'fa-solid fa-circle-dot' : 'fa-regular fa-circle';
      return html`<span @click=${eventClick} class="ticket-owner-check">
        <i class="fa ${icon}"></i>
        ${label}
      </span>`;
    };
    return html`<div class="support-inbox-ticket-owner">
      ${checkTemplate(tlang`All Tickets`, SupportInboxTicketOwnerFilter.All)}
      ${checkTemplate(tlang`My Tickets`, SupportInboxTicketOwnerFilter.Mine)}
      ${checkTemplate(tlang`Unassigned Tickets`, SupportInboxTicketOwnerFilter.Unassigned)}
    </div>`;
  }

  /** This is called by the wrapper when it wants new page data */
  async getRowsFromServer(request: RequestPage): Promise<ResultPaginated<ViewSupportInboxItemEx>> {
    const empty: ResultPaginated<ViewSupportInboxItemEx> = {
      count: 0,
      pageCount: 0,
      pageIndex: request.pageIndex,
      pageSize: request.pageSize,
      results: []
    };

    const result = await this.franchiseeApi.getQuoteSupportInbox({
      filter: this.filterText ?? '',
      requestPage: { pageIndex: request.pageIndex, pageSize: request.pageSize },
      sortAsc: false, // not used yet
      sortField: '', //not used yet
      statusFilter: this.statusFilter ?? SupportInboxItemStatus.All,
      ticketOwnerFilter: this.ticketOwner ?? SupportInboxTicketOwnerFilter.Mine,
      typeFilter: this.typeFilter ?? SupportInboxItemType.QuoteReview,
      branchIdFilter: this.activeTerritoryBranchIds ?? null
    });

    const items = result?.items;
    if (!items) return empty;

    //preload userids referenced by this page before the rows render
    const userIds = [
      ...items.results.map(x => x.item.lastModifiedUserId),
      ...items.results.map(x => x.item.franchiseeSystemUserId),
      ...items.results.map(x => x.quoteAssignedToUserId),
      ...items.results.map(x => x.quoteLastModifiedUserId)
    ];
    await cache().userProfile.preFetch(userIds);

    return items;
  }

  getColumns(): WebModuleTableColumnDef[] {
    // Column sizing moves from the Bootstrap `colpx-*` classes (which can't pierce
    // the new table's shadow DOM) to the column width model: fixed-content columns
    // get a `width`, text columns keep a `minWidth` floor and grow.
    const columns: WebModuleTableColumnDef[] = [];
    columns.push({
      title: tlang`%%franchisee%%`,
      minWidth: '160px',
      fieldName: 'name',
      fade: true,
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const rowItem = item as ViewSupportInboxItemEx;
        return this.nameOfTenant(rowItem.item.franchiseeTenantId);
      }
    });
    columns.push({
      title: tlang`%%branch%%`,
      minWidth: '120px',
      fieldName: 'branchName',
      fade: true,
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const rowItem = item as ViewSupportInboxItemEx;
        return this.nameOfBranch(rowItem.item.branchId);
      }
    });
    columns.push({
      title: tlang`%%franchisee%% %%quote%% Title`,
      minWidth: '150px',
      fieldName: 'quoteTitle',
      fade: true,
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const rowItem = item as ViewSupportInboxItemEx;
        const label = rowItem.title ?? '';
        if (rowItem.item.type == SupportInboxItemType.QuoteReview) {
          const clickItem = async (e: Event) => {
            e.stopPropagation();
            e.preventDefault();
            if (rowItem.quoteNumber === 0) {
              await information(
                tlang`Please wait and refresh your system. Quote information is not available yet.`,
                tlang`Data Unavailable`
              );
            }
            this.editQuoteOnFranchiseeSite(rowItem);
          };
          return html`<a href="#" @click=${clickItem}>${label}</a>`;
        } else {
          return html`${label}`;
        }
      }
    });
    columns.push({
      title: tlang`Ticket Title`,
      minWidth: '150px',
      fieldName: 'ticketTitle',
      fade: true,
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const rowItem = item as ViewSupportInboxItemEx;
        const label = !isEmptyOrSpace(rowItem.item.subject) ? rowItem.item.subject : this.nameOfQuote(rowItem);
        if (
          rowItem.item.type == SupportInboxItemType.EngineeredToOrder ||
          rowItem.item.type == SupportInboxItemType.QuoteAssistanceRequest
        ) {
          const clickItem = async (e: Event) => {
            e.stopPropagation();
            e.preventDefault();
            if (rowItem.quoteNumber === 0) {
              await information(
                tlang`Please wait and refresh your system. Quote information is not available yet.`,
                tlang`Data Unavailable`
              );
            }
            this.editQuoteOnFranchiseeSite(rowItem);
          };
          return html`<a href="#" @click=${clickItem}>${label}</a>`;
        } else {
          return html`${label}`;
        }
      }
    });
    columns.push({
      title: tlang`%%franchisee%% Author`,
      width: '140px',
      fieldName: 'author',
      fade: true,
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const rowItem = item as ViewSupportInboxItemEx;
        return cache().userProfile.getLocalData(rowItem.quoteAssignedToUserId)?.friendlyName ?? tlang`Unknown`;
      }
    });
    columns.push({
      title: tlang`Request Type`,
      width: '130px',
      fieldName: 'requestType',
      fade: true,
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const rowItem = item as ViewSupportInboxItemEx;
        return getSupportTypeDisplayValue(rowItem.item.type);
      }
    });
    columns.push({
      title: tlang`Status`,
      width: '130px',
      fieldName: 'status',
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const rowItem = item as ViewSupportInboxItemEx;
        return html`<span class="badge ${getSupportTypeStatusClass(rowItem.item.status)}"
          >${getSupportTypeStatus(rowItem.item.status)}</span
        >`;
      }
    });
    if (this.showAssignedTo) {
      columns.push({
        title: tlang`Assigned To`,
        width: '190px',
        fieldName: 'assignedTo',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewSupportInboxItemEx;
          const eventChangeUser = (e: CustomEvent<{ value: string }>) =>
            this.assignUserToSupportItem(rowItem, e.detail.value);
          return html` <webmodule-assignto
            .users=${allUserCache()}
            .value=${rowItem.item.assignedUserId}
            ?readonly=${!this.canChangeAssigment(rowItem)}
            ?disabled=${!this.canChangeAssigment(rowItem)}
            @wm-event-changed=${eventChangeUser}
          ></webmodule-assignto>`;
        }
      });
    }
    columns.push({
      title: tlang`Date Submitted`,
      width: '120px',
      fieldName: 'dateSubmitted',
      fade: true,
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const rowItem = item as ViewSupportInboxItemEx;
        return serverDateTimeToLocal(rowItem.item.dateCreated).toLocaleDateString();
      }
    });
    if (this.showLastModified) {
      columns.push({
        title: tlang`Last Modified`,
        minWidth: '200px',
        fieldName: 'lastModified',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewSupportInboxItemEx;
          const label = cache().userProfile.getLocalData(rowItem.item.lastModifiedUserId)?.friendlyName ?? '';
          const dateValue = serverDateTimeToLocalDateTime(rowItem.item.lastModified);

          const lDateValue =
            Math.abs(dateValue.diffNow().as('years')) > 1000 ? '' : dateValue.toLocaleString(DateTime.DATETIME_SHORT);
          // Date on the first line, the user on its own line below it in italics.
          return html`<div class="lastmodified-cell">
            <div class="lastmodified-date">${lDateValue}</div>
            <div class="lastmodified-user"><em>${label}</em></div>
          </div>`;
        }
      });
    }
    if (this.showResolvedColumns) {
      columns.push({
        title: tlang`Date Resolved`,
        width: '120px',
        fieldName: 'dateResolved',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewSupportInboxItemEx;
          return rowItem.item.status === SupportInboxItemStatus.Resolved
            ? serverDateTimeToLocal(rowItem.item.lastModified).toLocaleDateString()
            : '-';
        }
      });
      columns.push({
        title: tlang`Resolution`,
        width: '110px',
        fieldName: 'resolution',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewSupportInboxItemEx;
          return rowItem.item.status === SupportInboxItemStatus.Resolved
            ? `${serverDateTimeToLocalDateTime(rowItem.item.lastModified)
                .diff(serverDateTimeToLocalDateTime(rowItem.item.dateCreated), 'hours')
                .hours.toFixed(1)}hrs`
            : '-';
        }
      });
      columns.push({
        title: tlang`Reviewer`,
        width: '140px',
        fieldName: 'reviewer',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewSupportInboxItemEx;
          const reviewerUserId = rowItem.item.assignedUserId;
          const allUsers = allUserCache();
          return allUsers.find(u => u.id == reviewerUserId)?.friendlyName ?? '';
        }
      });
    }

    return columns;
  }

  canChangeAssigment(item: ViewSupportInboxItemEx): boolean {
    if (item.item.status === SupportInboxItemStatus.Cancelled || item.item.status === SupportInboxItemStatus.Resolved)
      return false;
    return true;
  }

  async assignUserToSupportItem(item: ViewSupportInboxItemEx, supplierSystemUserId: string | null) {
    if (!this.canChangeAssigment(item)) {
      await information('%%user%% assignment', tlang`This cannot be assigned at this time`);
      return;
    }

    await lockUIandExecute(async () => {
      const result = await getApiFactory()
        .franchisee()
        .updateSupportInboxItem({
          id: item.item.id,
          assignedUserId: supplierSystemUserId,
          status: !supplierSystemUserId
            ? SupportInboxItemStatus.New //revert to new if we unassign
            : item.item.status === SupportInboxItemStatus.New || item.item.status === SupportInboxItemStatus.Overdue
              ? SupportInboxItemStatus.Active //make active if new
              : item.item.status // leave where it is
        });
      if (result?.supplierSupportInboxItem) {
        item.item = result.supplierSupportInboxItem;
      }
    });
    // Force the table to re-render the mutated row.
    // eslint-disable-next-line no-self-assign
    if (this.dataTable) {
      const d = this.dataTable.data;
      this.dataTable.data = [];
      this.dataTable.data = d;
    }
  }

  editQuoteOnFranchiseeSite(item: ViewSupportInboxItemEx) {
    this.dispatchCustom('quotereview', {
      item: item
    });
  }

  nameOfQuote(item: ViewSupportInboxItemEx) {
    if (item.quoteNumber === 0) return tlang`<unavailable>`;
    return getQuoteNumberDisplay(item.quoteNumber, item.quoteSuffix);
  }

  nameOfBranch(branchId: string): string | TemplateResult {
    return (
      userDataStore.allFranchiseeDetails.branches?.find(x => x.branchId === branchId)?.abbreviation ?? tlang`Unknown`
    );
  }

  nameOfTenant(franchiseeTenantId: number): string {
    return (
      userDataStore.allFranchiseeDetails.tenantList.tenants?.find(x => x.id === franchiseeTenantId)?.companyName ??
      tlang`Unknown`
    );
  }

  dispatchCustom<T>(name: string, values: T): T {
    const options = {
      detail: { ...values },
      bubbles: true,
      composed: true
    };
    //wm-be webmodule-brancheditor
    this.dispatchEvent(new CustomEvent(`wm-supportinbox-${name}`, options));
    return values;
  }

  registerSSEListener(): void {
    const event = stagedExecution({
      threshold: defaultStagedThreshold,

      eventFinally: () => notificationSignal(false),
      event: () => {
        this.refreshData();
      },
      eventTriggered: (_data: SSESupportInbox) => {
        notificationSignal(true);
      },
      eventActioned: (_data: SSESupportInbox) => {
        const isVisible = this.lastFetch.find(x => x.item.id === _data.id);
        if (isVisible)
          lockUIandExecute(async () => {
            await this.refreshData();
          });
      }
    });
    this.sseHandler = event;
    WMEventSource.getInstance().addEventListener(WMEventSource.supportInbox, this.sseHandler);
  }

  registerUserRefreshSSEListener(): void {
    const event = stagedExecution({
      threshold: defaultStagedThreshold,

      eventFinally: () => notificationSignal(false),
      event: async () => {
        await this.refreshUserCacheAndTable();
      },
      eventTriggered: (_data: SSEUserRefresh) => {
        notificationSignal(true);
      }
    });
    this.sseUserRefreshHandler = event;
    WMEventSource.getInstance().addEventListener(WMEventSource.userRefresh, this.sseUserRefreshHandler);
  }

  private async refreshUserCacheAndTable(): Promise<void> {
    await refreshAllUserCache(true);
    this.dataTable?.requestUpdate();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.registerSSEListener();
    this.registerUserRefreshSSEListener();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.sseHandler) WMEventSource.getInstance().removeEventListener(WMEventSource.supportInbox, this.sseHandler);
    this.sseHandler = undefined;
    if (this.sseUserRefreshHandler)
      WMEventSource.getInstance().removeEventListener(WMEventSource.userRefresh, this.sseUserRefreshHandler);
    this.sseUserRefreshHandler = undefined;
  }
}
